import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import api from '../../services/api';

/**
 * Fetches the global SEO settings once and injects:
 *  - Google Search Console / Bing verification meta tags
 *  - GA4, GTM, Meta Pixel snippets
 *  - Site-wide Organization JSON-LD
 *  - Admin-authored header/body-start/body-end/footer custom code
 * Mount this ONCE near the root of the app (see App.js), not per-page.
 */
const GlobalSeoInjector = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/seo-settings/public')
      .then((res) => { if (mounted && res.data.success) setSettings(res.data.data); })
      .catch(() => {}); // Non-critical - page still works without it
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!settings) return;
    const cleanups = [];

    // Custom code fields (header/body-start/body-end/footer) are
    // admin-authored raw HTML/script snippets. Helmet only reliably
    // manages simple <head> tags, so arbitrary markup is inserted directly
    // via DOM APIs instead, each into its intended location.
    const inject = (html, target, prepend = false) => {
      if (!html) return;
      const container = document.createElement('div');
      container.innerHTML = html;
      const nodes = Array.from(container.childNodes);
      nodes.forEach((node) => {
        if (prepend && target.firstChild) target.insertBefore(node, target.firstChild);
        else target.appendChild(node);
      });
      cleanups.push(() => nodes.forEach((node) => node.parentNode && node.parentNode.removeChild(node)));
    };

    inject(settings.header_code, document.head);
    inject(settings.body_start_code, document.body, true);
    inject(settings.body_end_code, document.body);
    inject(settings.footer_code, document.body);

    return () => cleanups.forEach((fn) => fn());
  }, [settings]);

  if (!settings) return null;

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: settings.org_business_name,
    url: settings.org_website_url,
    logo: settings.org_logo || undefined,
    email: settings.org_email || undefined,
    telephone: settings.org_phone || undefined,
    sameAs: settings.org_same_as || []
  };

  return (
    <Helmet>
      {settings.gsc_verification_code && (
        <meta name="google-site-verification" content={settings.gsc_verification_code} />
      )}
      {settings.bing_verification_code && (
        <meta name="msvalidate.01" content={settings.bing_verification_code} />
      )}

      {settings.ga_measurement_id && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${settings.ga_measurement_id}`} />
      )}
      {settings.ga_measurement_id && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${settings.ga_measurement_id}');
        `}</script>
      )}

      {settings.gtm_id && (
        <script>{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${settings.gtm_id}');
        `}</script>
      )}

      {settings.meta_pixel_id && (
        <script>{`
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
          n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
          document,'script','https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${settings.meta_pixel_id}');
          fbq('track', 'PageView');
        `}</script>
      )}

      {settings.org_business_name && (
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      )}
    </Helmet>
  );
};

export default GlobalSeoInjector;
